<template>
    <subscriptions-bank-cards-store
        v-slot="{
            sid,
            store,
            data: card,
            error,
            is_loading: is_saving
        }"
        :params="{
            subscription: 'default'
        }"
        @success="onSuccess"
        @error="onError"
    >
        <base-form
            @submit="store(form, $refs.card_number.$refs.element._element)"
            class="bg-white rounded shadow-lg"
        >
            <header class="flex items-center px-4 border-b h-18 sm:px-6">
                <h1 class="flex-grow text-lg font-normal">Payment Method</h1>

                <div class="flex-none w-6 h-6 mr-3">
                    <base-svg
                        name="icon-lock"
                        class="w-full h-full"
                    />
                </div>
            </header>

            <div class="px-6 pt-6 border-b">
                <form-section>
                    <template slot="title">Bank Card</template>
                    <template slot="description">
                        Enter your card details.
                    </template>

                    <grid-layout>
                        <grid-layout-item>
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="name"
                            >
                                <template slot="label">Name On Card</template>

                                <input-text
                                    v-model="form.name"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    name="name"
                                />
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="sm:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="bank_card[number]"
                                validator-name="bank_card"
                            >
                                <template slot="label">Number</template>

                                <div class="relative">
                                    <card-number
                                        ref="card_number"
                                        :stripe="$stripe"
                                        :options="stripe_options"
                                        :class="{
                                            'rounded bg-grey-bg': is_saving
                                        }"
                                    />

                                    <div
                                        v-if="is_saving"
                                        class="absolute inset-0 cursor-not-allowed"
                                    />
                                </div>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="sm:w-1/2">
                            <grid-layout>
                                <grid-layout-item class="w-7/12">
                                    <form-item
                                        :sid="sid"
                                        :is-required="true"
                                        name="bank_card[expiry]"
                                    >
                                        <template slot="label">Expiry</template>

                                        <div class="relative">
                                            <card-expiry
                                                :stripe="$stripe"
                                                :options="stripe_options"
                                                :class="{
                                                    'rounded bg-grey-bg': is_saving
                                                }"
                                            />

                                            <div
                                                v-if="is_saving"
                                                class="absolute inset-0 cursor-not-allowed"
                                            />
                                        </div>
                                    </form-item>
                                </grid-layout-item>

                                <grid-layout-item class="w-5/12">
                                    <form-item
                                        :sid="sid"
                                        :is-required="true"
                                        name="bank_card[cvc]"
                                    >
                                        <template slot="label">CVC</template>

                                        <div class="relative">
                                            <card-cvc
                                                :stripe="$stripe"
                                                :options="stripe_options"
                                                :class="{
                                                    'rounded bg-grey-bg': is_saving
                                                }"
                                            />

                                            <div
                                                v-if="is_saving"
                                                class="absolute inset-0 cursor-not-allowed"
                                            />
                                        </div>
                                    </form-item>
                                </grid-layout-item>
                            </grid-layout>
                        </grid-layout-item>
                    </grid-layout>
                </form-section>
            </div>

            <div class="px-6 pt-6 border-b">
                <form-section>
                    <template slot="title">Billing Address</template>
                    <template slot="description">
                        The current billing address for the card.
                    </template>

                    <grid-layout>
                        <grid-layout-item>
                            <form-items-address
                                v-model="form.address"
                                :sid="sid"
                                :disabled="is_saving"
                            />
                        </grid-layout-item>
                    </grid-layout>
                </form-section>
            </div>

            <base-form-footer class="rounded-b">
                <base-button
                    :is-loading="is_saving"
                    class="w-full btn-blue md:w-auto"
                >
                    Update
                </base-button>
            </base-form-footer>
        </base-form>
    </subscriptions-bank-cards-store>
</template>

<script>
    import _ from 'lodash'
    import scroll from '../../../../support/scroll'
    import { CardNumber, CardExpiry, CardCvc } from 'vue-stripe-elements-plus'
    import FormItemsAddress from '../../../../components/form-items-address'
    import SubscriptionsBankCardsStore from '../../../../components/subscriptions-bank-cards-store'

    export default {
        components: {
            CardNumber,
            CardExpiry,
            CardCvc,
            FormItemsAddress,
            SubscriptionsBankCardsStore
        },
        data () {
            return {
                show_created_modal: false,
                form: {
                    name: '',
                    address: {
                        street: _.preload('address.data.street'),
                        locality: _.preload('address.data.locality'),
                        village: _.preload('address.data.village'),
                        town: _.preload('address.data.town'),
                        county: _.preload('address.data.county'),
                        postcode: _.preload('address.data.postcode')
                    }
                }
            }
        },
        computed: {
            stripe_options () {
                return {
                    classes: {
                        base: 'input',
                        complete: 'is-complete',
                        empty: 'is-empty',
                        focus: 'is-focused',
                        invalid: 'is-invalid'
                    },
                    style: {
                        base: {
                            color: '#212934',
                            fontFamily: 'Nunito, sans-serif',
                            fontSize: '16px',
                            fontWeight: '400',
                            '::placeholder': {
                                color: '#CFD7E0'
                            },
                            ':disabled': {
                                backgroundColor: '#F00'
                            }
                        },
                        invalid: {
                            iconColor: '#f00',
                            color: '#1f2d3d'
                        }
                    }
                }
            }
        },
        created () {
            this.$stripe.elements({
                fonts: [
                    { cssSrc: 'https://fonts.googleapis.com/css?family=Nunito:400' }
                ]
            })
        },
        methods: {
            onSuccess () {
                this.$track(
                    'dashboard.landlords.subscriptions.create',
                    'form-submission',
                    'A landlord has subscribed'
                )

                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            },
            onError () {
                scroll.toTop()
            }
        }
    }
</script>
